// Doctor-wise appointments: the weekly sessions of a doctor, as the schedule editor handles them.
// The server is the authority; these checks only tell staff what is wrong before they press Save.

export const MAX_SESSIONS = 28;
export const MIN_SLOT_MINUTES = 5;
export const MAX_SLOT_MINUTES = 240;

export const newSession = (weekday = 1) => ({ weekday, startTime: '09:00', endTime: '13:00', slotMinutes: 30 });

const toMinutes = (time) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time || '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
};

/** How many whole slots a session holds (a part-slot at the end is not offered). */
export const slotCount = (row) => {
  const start = toMinutes(row.startTime);
  const end = toMinutes(row.endTime);
  const step = Number(row.slotMinutes);
  if (!(end > start) || !(step > 0)) return 0;
  return Math.floor((end - start) / step);
};

/** The first problem in a schedule, or null when it can be saved. */
export const validateSessions = (rows) => {
  if (rows.length > MAX_SESSIONS) return `A doctor can have at most ${MAX_SESSIONS} sessions a week.`;
  for (const r of rows) {
    const start = toMinutes(r.startTime);
    const end = toMinutes(r.endTime);
    const step = Number(r.slotMinutes);
    if (!Number.isInteger(Number(r.weekday)) || r.weekday < 0 || r.weekday > 6) return 'Choose a weekday for every session.';
    if (Number.isNaN(start) || Number.isNaN(end)) return 'Enter a start and an end time for every session.';
    if (!(step >= MIN_SLOT_MINUTES && step <= MAX_SLOT_MINUTES)) return `Slot length must be between ${MIN_SLOT_MINUTES} and ${MAX_SLOT_MINUTES} minutes.`;
    if (end <= start) return 'A session must end after it starts.';
    if (end - start < step) return 'A session must be at least one slot long.';
  }
  const byDay = new Map();
  rows.forEach((r) => byDay.set(Number(r.weekday), [...(byDay.get(Number(r.weekday)) || []), r]));
  for (const day of byDay.values()) {
    const ordered = [...day].sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
    for (let i = 1; i < ordered.length; i += 1) {
      if (toMinutes(ordered[i].startTime) < toMinutes(ordered[i - 1].endTime)) return 'Two sessions on the same weekday overlap.';
    }
  }
  return null;
};

export const sessionsPayload = (rows) => rows.map((r) => ({
  weekday: Number(r.weekday), startTime: r.startTime, endTime: r.endTime, slotMinutes: Number(r.slotMinutes),
}));

export const sortSessions = (rows) => [...rows].sort((a, b) => (Number(a.weekday) - Number(b.weekday)) || (toMinutes(a.startTime) - toMinutes(b.startTime)));

/** One sentence above the list of appointments a schedule or leave change leaves without a valid slot. */
export const conflictHeadline = (count) =>
  `${count} booked appointment${count === 1 ? '' : 's'} will no longer match this doctor's slots. They are not cancelled or moved: staff must reschedule ${count === 1 ? 'it' : 'them'}.`;
